// src/features/task/create/propertyTrigger.ts
// The look of a property trigger, on its own.
//
// Most properties open a popover and go through PropertyPill. One does not:
// picking a task type opens the list of types directly, because the type is the
// property that reshapes the form and a popover holding a picker made that two
// decisions instead of one. It still has to read as the same panel row, and a
// second copy of these classes is how two rows start disagreeing about what
// "chosen" looks like.

import { cn } from '@core/ui';

export type PropertyTriggerVariant = 'pill' | 'quiet' | 'row';

export interface PropertyTriggerState {
  hasValue: boolean;
  open: boolean;
  invalid?: boolean;
  /** Required and still empty — marked before Create is ever pressed. */
  missingRequired?: boolean;
}

export function propertyTriggerClass(
  variant: PropertyTriggerVariant,
  { hasValue, open, invalid, missingRequired }: PropertyTriggerState,
): string {
  if (variant === 'quiet') {
    return cn(
      'inline-flex max-w-full items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      hasValue ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
      'hover:bg-muted',
      open && 'bg-muted text-foreground',
      'disabled:cursor-not-allowed disabled:opacity-50',
    );
  }
  if (variant === 'row') {
    return cn(
      // A panel row is a full-width value, not a chip: the label lives outside
      // it (PropertyRow), so the trigger carries only the answer.
      'flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-sm transition-colors',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      'disabled:cursor-not-allowed disabled:opacity-50',
      hasValue ? 'text-foreground' : 'text-muted-foreground',
      open
        ? 'border-primary/60 bg-muted text-foreground'
        : 'border-transparent hover:border-border hover:bg-muted hover:text-foreground',
      missingRequired && !open && 'border-dashed border-destructive/50',
      invalid && 'border-solid border-destructive text-destructive',
    );
  }
  return cn(
    'inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
    'disabled:cursor-not-allowed disabled:opacity-50',
    hasValue
      ? 'border-border bg-muted/60 text-foreground'
      : 'border-dashed border-border text-muted-foreground',
    'hover:border-primary/50 hover:bg-muted hover:text-foreground',
    open && 'border-primary/60 bg-muted text-foreground',
    invalid && 'border-destructive text-destructive hover:border-destructive',
  );
}
